import i18n from '@/i18n';
import { useTheme } from '@/hooks/useTheme';

/**
 * Токени теми для розділу «Фінанси». Окрема копія в межах розділу (клон, не
 * спільне) — світла/темна, як market/prices. Темні значення збігаються з
 * forecast-mms, щоб два тестові модулі виглядали однаково.
 */
export function useFinTheme() {
  const { isDark } = useTheme();
  return {
    isDark,
    pageBg:    isDark ? '#111113' : '#F2F2F5',
    cardBg:    isDark ? '#1A1A1A' : '#FFFFFF',
    cardBorder:isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8',
    innerBg:   isDark ? '#0F0F10' : '#F5F5F7',
    textMain:  isDark ? '#E8E8EA' : '#1A1A1A',
    textMuted: isDark ? '#8A8A93' : '#555560',
    textDim:   isDark ? '#5A5A62' : '#9B9BA4',
    rowHover:  isDark ? 'rgba(255,255,255,0.04)' : '#F0F1F3',
    sep:       isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)',
    inputScheme: (isDark ? 'dark' : 'light') as 'dark' | 'light',

    ok:    '#22C55E',
    warn:  '#EAB308',
    overlay: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.35)',
    okText:   isDark ? '#86EFAC' : '#166534',
    warnText: isDark ? '#FDE68A' : '#92400E',
    errText:  isDark ? '#FCA5A5' : '#991B1B',
  };
}

export type FinTheme = ReturnType<typeof useFinTheme>;

// Спільні типи довідника ─────────────────────────────────────────────────
export interface SaleTerm {
  id: number;
  asset_id: string;
  asset_name?: string | null;
  seller_name?: string | null;
  buyer_type: string;                 // guaranteed | trader | aggregator
  buyer_name?: string | null;
  contract_number?: string | null;
  contract_date?: string | null;
  valid_from: string;
  valid_to: string;
  sale_type: string;                  // green_tariff | bilateral
  green_tariff_until?: string | null;
  nkrekp_ref?: string | null;
  price_model: string;                // fixed | formula
  fixed_price_uah_mwh?: number | string | null;
  base_index?: string | null;         // rdn | other
  markup_pct?: number | string | null;
  currency: string;
  price_unit: string;
  vat_included: boolean;
  status: string;                     // draft | active | completed
  created_by?: string | null;
  updated_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface AssetLite { id: string; assetName: string }

export const BUYER_TYPES: { value: string; label: string }[] = [
  { value: 'guaranteed', get label() { return i18n.t('ak_fin_guaranteed_buyer'); } },
  { value: 'trader',     get label() { return i18n.t('ak_fin_trader'); } },
  { value: 'aggregator', get label() { return i18n.t('ak_fin_aggregator'); } },
];
export const SALE_TYPES: { value: string; label: string }[] = [
  { value: 'green_tariff', get label() { return i18n.t('ak_fin_green_tariff'); } },
  { value: 'bilateral',    get label() { return i18n.t('ak_fin_bilateral_contract'); } },
];
export const PRICE_MODELS: { value: string; label: string }[] = [
  { value: 'fixed',   get label() { return i18n.t('ak_fin_fixed'); } },
  { value: 'formula', get label() { return i18n.t('ak_fin_formula'); } },
];
export const BASE_INDEXES: { value: string; label: string }[] = [
  { value: 'rdn',   get label() { return i18n.t('ak_fin_dam'); } },
  { value: 'other', get label() { return i18n.t('ak_fin_other_index'); } },
];
export const STATUSES: { value: string; label: string; color: string }[] = [
  { value: 'draft',     get label() { return i18n.t('ak_fin_draft'); },  color: '#8A8A93' },
  { value: 'active',    get label() { return i18n.t('ak_fin_active'); },    color: '#22C55E' },
  { value: 'completed', get label() { return i18n.t('ak_fin_completed'); }, color: '#6B7280' },
];

export const labelOf = (list: { value: string; label: string }[], v?: string | null) =>
  list.find((x) => x.value === v)?.label ?? (v || '—');

/** ISO-datetime або date → 'YYYY-MM-DD' для <input type=date>. */
export const toDateInput = (v?: string | null) => (v ? String(v).slice(0, 10) : '');
/** 'YYYY-MM-DD' → 'DD.MM.YYYY' для показу. */
export const dmy = (v?: string | null) => {
  const d = toDateInput(v);
  return d ? d.split('-').reverse().join('.') : '—';
};
